import React, { useMemo, useState } from 'react';
import { MessageCircle, Phone, Plus, Upload } from 'lucide-react';
import { VendorDrawer } from './VendorDrawer';
import { VendorImport } from './VendorImport';
import { Drawer, field, ghost, primary, rs, Stat, useApi } from './ui';

const FILTERS = [['all', 'All'], ['active', 'Active'], ['dues', 'Has dues'], ['overdue', 'Overdue'], ['gstin', 'GSTIN expiring']];

const VendorCard = ({ id, onClose }) => {
  const { call } = useApi();
  const [d, setD] = useState(null);
  React.useEffect(() => { call('get', `/purchasing/vendors/${id}`).then(setD); }, [id, call]);
  return (
    <Drawer title={d?.name || 'Vendor'} onClose={onClose} testid="vendor-card">
      {d && <>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Total purchased" value={rs(d.purchased)} testid="vc-purchased" />
          <Stat label="Owed" value={rs(d.owed)} testid="vc-owed" />
          <Stat label="Overdue" value={rs(d.overdue)} tone="text-red-600" testid="vc-overdue" />
          <Stat label="On-time delivery" value={d.on_time_pct == null ? '—' : `${d.on_time_pct}%`} testid="vc-ontime" />
        </div>
        <h3 className="text-sm font-bold">Last price paid</h3>
        <table className="w-full text-sm" data-testid="vc-prices">
          <tbody>{d.last_prices.map((p) => <tr key={p.name} className="border-t"><td className="py-1.5">{p.name}</td><td className="text-right">{rs(p.rate)}</td><td className="text-right text-slate-500">{p.date}</td></tr>)}</tbody>
        </table>
        {!d.last_prices.length && <p className="text-sm text-slate-500">No bills yet.</p>}
      </>}
    </Drawer>
  );
};

export const VendorsTab = ({ vendors, items, reload }) => {
  const [q, setQ] = useState('');
  const [f, setF] = useState('all');
  const [edit, setEdit] = useState(null);
  const [card, setCard] = useState(null);
  const [imp, setImp] = useState(false);
  const rows = useMemo(() => vendors.filter((v) => (!q || `${v.name} ${v.mobile} ${v.gstin}`.toLowerCase().includes(q.toLowerCase()))
    && (f === 'all' || (f === 'active' && v.active) || (f === 'dues' && v.owed > 0) || (f === 'overdue' && v.overdue > 0) || (f === 'gstin' && v.gstin_expiring))), [vendors, q, f]);
  return (
    <div className="space-y-4" data-testid="vendors-tab">
      <div className="flex flex-wrap items-center gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, mobile, GSTIN" className={`${field} max-w-xs`} data-testid="vendor-search" />
        {FILTERS.map(([k, l]) => <button key={k} onClick={() => setF(k)} className={`rounded-full px-3 py-1 text-xs font-semibold ${f === k ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 border'}`} data-testid={`vendor-filter-${k}`}>{l}</button>)}
        <button className={`${ghost} ml-auto`} onClick={() => setImp(true)} data-testid="vendor-import-open"><Upload size={15} /> Import from Excel</button>
        <button className={primary} onClick={() => setEdit({})} data-testid="vendor-add"><Plus size={15} /> Add vendor</button>
      </div>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {rows.map((v) => (
          <div key={v.id} className="rounded-xl border border-slate-200 bg-white p-4 transition-shadow hover:shadow-md" data-testid={`vendor-row-${v.id}`}>
            <div className="flex items-start justify-between gap-2">
              <button onClick={() => setCard(v.id)} className="text-left" data-testid={`vendor-open-${v.id}`}><p className="font-semibold text-slate-900">{v.name}</p><p className="text-xs text-slate-500">{v.gstin || 'No GSTIN'} · {v.city || v.state || '—'}</p></button>
              {!v.active && <span className="rounded-full bg-slate-100 px-2 text-[11px]">Inactive</span>}
            </div>
            <div className="mt-3 flex items-center gap-3 text-sm">
              <span>Owed <b>{rs(v.owed)}</b></span>{v.overdue > 0 && <span className="text-red-600">Overdue {rs(v.overdue)}</span>}
              {v.gstin_expiring && <span className="text-amber-700 text-xs">GSTIN expiring</span>}
            </div>
            <div className="mt-3 flex gap-2">
              <a href={`tel:+91${v.mobile}`} className={ghost} data-testid={`vendor-call-${v.id}`}><Phone size={14} /></a>
              <a href={`https://wa.me/91${String(v.mobile).slice(-10)}`} target="_blank" rel="noreferrer" className={ghost} data-testid={`vendor-wa-${v.id}`}><MessageCircle size={14} /></a>
              <button className={ghost} onClick={() => setEdit(v)} data-testid={`vendor-edit-${v.id}`}>Edit</button>
            </div>
          </div>
        ))}
      </div>
      {!rows.length && <p className="py-10 text-center text-sm text-slate-500" data-testid="vendors-empty">No vendors yet. Add your first supplier.</p>}
      {edit && <VendorDrawer vendor={edit} items={items} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />}
      {card && <VendorCard id={card} onClose={() => setCard(null)} />}
      {imp && <Drawer title="Import vendors and past bills" onClose={() => setImp(false)} testid="vendor-import-drawer"><VendorImport compact onDone={reload} /></Drawer>}
    </div>
  );
};
